import type { IProductVisited } from '@Components/Modals/SeenThings/SeenThingsPresenter';
import { useClassStore } from '@Utils/useClassStore';
import { inject, injectable, postConstruct } from 'inversify';
import { action, observable, reaction, when } from 'mobx';
import { createRef } from 'react';
import type { ScrollView } from 'react-native';

import { LocalStorageGateway } from '~gateways/local-storage';
import container from '~ioc/ioc';
import { AnalyticsStore } from '~stores/analytics';
import AuthStore from '~stores/authentication';
import { NavigationStore } from '~stores/navigation';
import productsRepository from '~stores/products-repository';
import type { AnalyticsEventData } from '~types/analytics';
import type {
  INavigationNode,
  IProductDetail,
  IProductOption,
  ProductActionType,
  UserLists,
} from '~types/models-types';
import type { INavigationLeaf, ScreenSize } from '~types/views-types';
import { ViewModes } from '~types/views-types';

// TODO: Is this store needed? If yes, it still has a lot of clean up that can be done
@injectable()
class ProductsStore {
  @inject(AuthStore)
  private AuthStore!: AuthStore;
  @inject(NavigationStore) private navigator2Store!: NavigationStore;
  @inject(AnalyticsStore) private _analyticsStore!: AnalyticsStore;
  @inject(LocalStorageGateway)
  private localStorageGateway!: LocalStorageGateway;
  @observable navigationMap: (INavigationLeaf | INavigationNode)[] | null =
    null;
  @observable myOwnableNavigationMap:
    | (INavigationLeaf | INavigationNode)[]
    | null = null;
  @observable ownableAccountMap: (INavigationLeaf | INavigationNode)[] | null =
    null;

  @observable loadedSize: ScreenSize | null = null;
  @observable uiProductDetailOptionMenuOpen = false;
  @observable uiProductSectionInitialScroll: boolean = false;
  @observable uiProductListCount = 0;
  @observable uiProductCardsPerRow: number | undefined = undefined;
  @observable uiProductSectionWidth: number | undefined = undefined;

  @observable currentProduct: IProductDetail | null = null;
  @observable currentProductSelectedOption: IProductOption | null = null;
  @observable currentUserLists: UserLists = [];
  @observable productAction: ProductActionType | undefined = undefined;
  @observable lastProductActionId: number | undefined = undefined;

  @observable showProductSectionScrollLoadingIndicator: boolean = false;

  @observable currentProductLoading = true;
  @observable productsLoading = true;

  @observable uiSortMenuOpen = false;
  @observable uiSecondaryMenuOpen = false;

  @observable currentProductSectionRef: any = undefined;
  @observable rowMap: number[] = [];
  @observable productScrollRef = createRef<ScrollView>();

  @observable uiTagMenuOpen = false;

  @observable uiShareProductByEmailOverlayOpen = false;

  // from root store
  @observable uiHeaderCenterControlsVisible = false;
  @observable primaryContextHeader: string = '';
  @observable secondaryContextHeader: string = '';
  @observable viewMode = ViewModes.bypage;
  @observable uiEditionSelectionMenuOpen = false;
  @observable uiOverlayOpen = false;

  @observable uiNavDrawerHidden = false;
  @observable uiSupportPanelOpen = false;
  @observable currentIssueName: string | undefined = undefined;
  @observable currentTitleName: string | undefined = undefined;

  // navigation store

  @observable uiQuickTipsPanelOpen = false;
  @observable uiAboutPanelOpen = false;

  @action scrollToBuy = () => {
    this.productScrollRef?.current?.scrollTo?.({
      x: 0,
      y: 900,
      animated: true,
    });
  };

  @postConstruct() init() {
    reaction(
      () => this.navigator2Store.currentParams?.value?.productId,
      (productId) => {
        if (productId) {
          this.getProductData(productId).then();
          this.logProductViewAnalytics(productId).then();
          this.saveProduct(productId).then();
        } else {
          this.setCurrentProduct(null);
        }
      },
      { fireImmediately: true }
    );
  }

  private addProductIntoLocalStorage = (product: IProductVisited) => {
    let products =
      this.localStorageGateway.getItem<IProductVisited[]>('products') ?? [];
    products = products.filter((el) => el.productId !== product.productId);
    products.unshift(product);
    this.localStorageGateway.saveItem('products', products);
  };

  private saveProduct = async (productId: number) => {
    try {
      await when(() => this.currentProduct?.id === productId, {
        timeout: 10000,
      });

      if (this.currentProduct) {
        const currentDate = new Date().toDateString();
        this.addProductIntoLocalStorage({
          productId: this.currentProduct.id,
          productName: this.currentProduct.name,
          brandName: this.currentProduct.brand,
          productImage: this.currentProduct.mediaUrls[0],
          path: this.navigator2Store.currentParams.value,
          date: currentDate,
        });
      }
    } catch (error) {
      // Error can be silent. This function only saves the visited products to localstorage
    }
  };

  @action
  public readonly handleHeaderControlsVisible = (
    newState: boolean = !this.uiHeaderCenterControlsVisible
  ) => {
    this.uiHeaderCenterControlsVisible = newState;
  };

  @action handleAppLoadScreenSize = (size: ScreenSize) => {
    this.loadedSize = size;
  };

  @action public readonly setUiShareProductByEmailOverlayOpen = (
    newState: boolean = !this.uiShareProductByEmailOverlayOpen
  ) => (this.uiShareProductByEmailOverlayOpen = newState);

  @action readonly setCurrentProductSelectedOption = (
    selection: IProductOption | null = null
  ) => {
    this.currentProductSelectedOption = selection;
  };

  @action
  public readonly setViewMode = (viewMode: ViewModes) => {
    this.viewMode = viewMode;
  };

  @action
  public readonly getProductData = async (productId: number) => {
    this.currentProductLoading = true;

    const productDetail = await productsRepository.loadProductAsync(productId);
    this.setCurrentProduct(productDetail);

    this.uiProductSectionInitialScroll = true;
    let defaultOption = null;

    if (productDetail?.options.length) {
      defaultOption = productDetail.options.find((o) => o.default) ?? null;
    }
    this.currentProductSelectedOption = defaultOption;
    this.currentProductLoading = false;
  };

  @action
  public readonly updateLastProductActionId = (id: number | undefined) => {
    this.lastProductActionId = id;
  };

  @action public readonly setProductsLoading = (setting: boolean) =>
    (this.productsLoading = setting);

  @action public readonly setCurrentProduct = (
    product: IProductDetail | null
  ) => (this.currentProduct = product);

  private logProductViewAnalytics = async (productId?: number) => {
    if (productId) {
      await when(() => this.currentProduct?.id === productId, {
        timeout: 10000,
      });

      const logData: AnalyticsEventData<'view_product_details'> = {
        userId: this.AuthStore.currentUser?.id,
        productId,
        productName: this.currentProduct?.name!,
      };

      this._analyticsStore.logViewProductDetails(logData);
    }
  };
}

export const useProductsStore = () =>
  useClassStore<ProductsStore>(container.get(ProductsStore));

export default ProductsStore;
